import { Avatar, Box, Grid, capitalize } from "@mui/material";
import React from "react";
import Text from "../../../../../../../../components/Text";
import { PatientProfile } from "../../../../../../../../hooks/local-storage/use-selected-patient";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import { getAddressFormat } from "../../../../../../../../utils/utils";
import { SUBSTITUTIONS_OR_DISPENSE_OPTIONS } from "../../../patient-chart/order/order-utils";
import { CHART_COLORS } from "../../../../../../../../components/app/provider/patient/chart";
import { PRIMITIVE, px } from "../../../../../../../../tokens/design-tokens";

interface PrescriptionPrintProps {
  formData: any;
  providerGroupProfileData: any;
  patientData: PatientProfile;
}

const PrescriptionPrint: React.FC<PrescriptionPrintProps> = ({
  formData,
  providerGroupProfileData,
  patientData,
}) => {
  // Helper function to get first value from object
  const getFirstValue = (obj: any) => {
    if (!obj || typeof obj !== "object") return "";
    const keys = Object.keys(obj);
    return keys.length > 0 ? obj[keys[0]] : "";
  };

  // Prescribed medications live in `medicationData` (one entry per ordered
  // medication). For legacy prescriptions created via the single "Add Rx" flow
  // this is empty, so fall back to the top-level medication fields.
  const prescribedMedications = formData.medicationData
    ? Array.from(formData.medicationData as any[])
    : [];
  const medicationList =
    prescribedMedications.length > 0 ? prescribedMedications : [formData];

  // Pharmacy is picked while ordering the Rx, so it comes with the prescription
  // itself and not from the patient's saved preferences.
  const pharmacyDetails = formData.pharmacyDetails;

  return (
    <Box
      sx={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        margin: "0 auto",
        backgroundColor: CHART_COLORS.surfaceWhite,
        color: PRIMITIVE.color.neutrals[2700],
        padding: px(PRIMITIVE.spacing[20]),
        fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
        lineHeight: "1.4",
        border: `1px solid ${PRIMITIVE.color.neutrals[2700]}`,
        borderRadius: "10px",
      }}
    >
      {/* Header Section : we need to get the header from the formData*/}
      <Box display="flex" alignItems="flex-start" marginBottom="20px" gap={2}>
        {/* Logo placeholder */}
        <Avatar
          src={providerGroupProfileData?.avatar}
          alt="Practice Logo"
          sx={{
            width: PRIMITIVE.spacing[60],
            height: PRIMITIVE.spacing[60],
            bgcolor: PRIMITIVE.color.neutrals[1000],
            border: `1px solid ${PRIMITIVE.color.neutrals[1200]}`,
          }}
        />

        {/* Practice Information */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          flex={1}
        >
          <Text
            variant="h1"
            fontWeight="bold"
            size="16px"
            style={{ marginBottom: "5px" }}
          >
            {providerGroupProfileData?.name}
          </Text>
          <Text variant="body2" size="11px" style={{ marginBottom: "2px" }}>
            {getAddressFormat(providerGroupProfileData?.physicalAddress)}
          </Text>
          {providerGroupProfileData?.phone && (
            <Text variant="body2" size="11px" style={{ marginBottom: "2px" }}>
              <strong>Phone #:</strong> {providerGroupProfileData.phone}
            </Text>
          )}
          {providerGroupProfileData?.fax && (
            <Text variant="body2" size="11px" style={{ marginBottom: "2px" }}>
              <strong>Fax #:</strong> {providerGroupProfileData.fax}
            </Text>
          )}
        </Box>
      </Box>

      {/* Prescriber Information */}
      <Box marginBottom="15px">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={0.5}
        >
          <Text
            variant="body2"
            fontWeight="bold"
            size="16px"
            style={{ marginBottom: "5px" }}
          >
            {getFirstValue(formData.provider)}
          </Text>
          <Box display="flex" flexDirection="row" gap={4} flexWrap="wrap">
            <Text variant="body2" size="11px">
              <strong>NPI:</strong> {formData.providerNpi || "-"}
            </Text>
            <Text variant="body2" size="11px">
              <strong>MD Lic#:</strong> {formData.providerLicenseNumber || "-"}
            </Text>
            <Text variant="body2" size="11px">
              <strong>DEA:</strong> {formData.providerDea || "-"}
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Dotted Line Divider */}
      <Box
        borderBottom={`1px dashed ${PRIMITIVE.color.neutrals[2700]}`}
        marginBottom="15px"
      />

      {/* Patient Details Section */}
      <Box
        marginBottom="15px"
        borderBottom={`1px solid ${PRIMITIVE.color.neutrals[2700]}`}
        paddingBottom="10px"
      >
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>Patient Name:</strong> {getFirstValue(formData.patient)}
            </Text>
          </Grid>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>DOB:</strong> {formatDate(patientData.birthDate)}
            </Text>
          </Grid>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>Mob#:</strong> {patientData.mobileNumber}
            </Text>
          </Grid>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>Gender:</strong> {patientData.gender}
            </Text>
          </Grid>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>Tel#:</strong> {patientData.mobileNumber}
            </Text>
          </Grid>
          <Grid item xs={4}>
            <Text variant="body2" size="11px">
              <strong>Address:</strong> {getAddressFormat(patientData.address)}
            </Text>
          </Grid>
        </Grid>
      </Box>

      {/* Rx Section */}
      <Box
        marginBottom="15px"
        borderBottom={`1px solid ${PRIMITIVE.color.neutrals[2700]}`}
        paddingBottom="10px"
      >
        <Text
          variant="body2"
          fontWeight="bold"
          size="14px"
          style={{ marginBottom: "15px" }}
        >
          Rx
        </Text>
        <Box
          sx={{
            border: `1px solid ${PRIMITIVE.color.neutrals[2700]}`,
            borderRadius: px(PRIMITIVE.radius[8]),
            padding: px(PRIMITIVE.spacing[12]),
            marginBottom: "15px",
            backgroundColor: CHART_COLORS.surfaceWhite,
          }}
        >
          {medicationList.map((medication: any, index: number) => (
            <Box
              key={medication?.uuid || index}
              sx={{
                marginBottom:
                  index < medicationList.length - 1
                    ? px(PRIMITIVE.spacing[12])
                    : 0,
              }}
            >
              <Text variant="body2" fontWeight="500" marginBottom="8px">
                {index + 1}. {getFirstValue(medication.medicationId)}
              </Text>
              <Text variant="body2">
                {`${medication.duration ? `Duration: ${medication.duration}` : ""}
                ${medication.quantity ? `Quantity: ${medication.quantity}` : ""}
                ${medication.unit ? `Unit: ${medication.unit}` : ""}
                ${medication.refills ? "Refills: " + medication.refills : ""}
                ${medication.startDate ? "Start Date: " + formatDate(medication.startDate) : ""}
                 ${medication.endDate ? "End Date: " + formatDate(medication.endDate) : ""}`.trim() ||
                  "-"}
              </Text>
              {medication.directionToPatient && (
                <Text variant="body2">
                  <strong>Directions to Patient:</strong>{" "}
                  {medication.directionToPatient}
                </Text>
              )}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            border: `1px solid ${PRIMITIVE.color.neutrals[2700]}`,
            borderRadius: px(PRIMITIVE.radius[8]),
            padding: px(PRIMITIVE.spacing[12]),
            marginBottom: "15px",
            backgroundColor: CHART_COLORS.surfaceWhite,
          }}
        >
          <Text variant="body2" fontWeight="500" marginBottom="8px">
            <strong>Substitutions or Dispense:</strong>{" "}
            {
              SUBSTITUTIONS_OR_DISPENSE_OPTIONS[
                formData.substitutionsOrDispense ?? ""
              ]
            }
          </Text>
          <Text variant="body2">
            <strong>Medical Codes:</strong>{" "}
            {formData.medicalCodes
              ? Object.values(formData.medicalCodes).join(" , ")
              : "-"}
          </Text>
        </Box>
      </Box>

      {/* Substitutions and Notes Section */}
      <Box
        marginBottom="15px"
        borderBottom={`1px solid ${PRIMITIVE.color.neutrals[2700]}`}
        paddingBottom="10px"
      >
        <Text
          variant="body2"
          size="10px"
          style={{ marginBottom: "5px", fontStyle: "italic" }}
        >
          In order for a brand name product to be dispensed, the prescriber must
          hand write 'Brand Necessary' or 'brand Medically Necessary' in the
          space below.
        </Text>
        <Box marginBottom="5px">
          <Text variant="body2">
            <strong>Pharmacist Note:</strong> {formData.directionToPharmacist}
          </Text>
        </Box>
        <Box marginTop="20px">
          {formData.providerSignature && (
            <Box
              component="img"
              src={formData.providerSignature}
              alt="Provider Signature"
              sx={{
                display: "block",
                height: px(PRIMITIVE.spacing[60]),
                maxWidth: "240px",
                objectFit: "contain",
                marginBottom: px(PRIMITIVE.spacing[4]),
                pageBreakInside: "avoid",
                breakInside: "avoid",
              }}
            />
          )}
          <Box
            borderTop={`1px solid ${PRIMITIVE.color.neutrals[2700]}`}
            paddingTop="10px"
          >
            <Text variant="body2">(Signature)</Text>
          </Box>
        </Box>
      </Box>

      {/* Pharmacy Details Section */}
      <Box
        marginBottom="15px"
        borderBottom={`1px solid ${PRIMITIVE.color.neutrals[2700]}`}
        paddingBottom="10px"
      >
        <Text variant="body2" fontWeight="bold" marginBottom="5px">
          Pharmacy Details:
        </Text>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Text>
              <strong>Name:</strong> {pharmacyDetails?.name || "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text>
              <strong>Preference Type:</strong>{" "}
              {capitalize(pharmacyDetails?.preferenceType ?? "") || "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text>
              <strong>Contact No#:</strong>{" "}
              {pharmacyDetails?.contactNumber || "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text>
              <strong>FAX:</strong> {pharmacyDetails?.faxNumber || "-"}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text>
              <strong>Address:</strong>{" "}
              {getAddressFormat(pharmacyDetails?.address) || "-"}
            </Text>
          </Grid>
        </Grid>
      </Box>

      {/* Patient Allergies Section */}
      <Box marginBottom="15px">
        <Text variant="body2" fontWeight="bold" marginBottom="10px">
          Patient Allergies:
        </Text>
        {formData.patientAllergies &&
        Array.isArray(formData.patientAllergies) &&
        formData.patientAllergies.length > 0 ? (
          <Grid container spacing={1}>
            {formData.patientAllergies.map((item: any, index: number) => (
              <Grid item xs={4} key={item?.uuid || index}>
                <Text>
                  {index + 1}. {item?.allergy}
                </Text>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Text variant="body2" size="11px">
            -
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default PrescriptionPrint;
